import { CommonModule } from '@angular/common';
import { Component, EventEmitter, inject, Input, Output } from '@angular/core';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { TableModule } from 'primeng/table';
import { finalize } from 'rxjs';

import { PlanoRecrutamentoService } from '@app/features-modules/bolsa-de-profissionais/services';

type ExcelPreviewValue = string | number | boolean | Date | null;

const PDRH_TEMPLATE_HEADERS = [
  'Nº Vagas',
  'Ano de Exercício',
  'Ano de Experiência',
  'Profissão',
  'Especialidade/Função',
  'Tipo de Contrato',
  'Nível de Habilitação',
  'Regime de Trabalho',
  'Descrição',
  'Razões do Recrutamento',
  'Descrição das Habilitações',
] as const;

@Component({
  selector: 'app-pdrh',
  standalone: true,
  imports: [CommonModule, ButtonModule, DialogModule, TableModule],
  templateUrl: './pdrh.component.html',
  styleUrl: './pdrh.component.scss',
})
export class PdrhComponent {
  private readonly messageService = inject(MessageService);
  private readonly planoRecrutamentoService = inject(PlanoRecrutamentoService);

  @Input({ required: true }) empresaId!: number | string | null | undefined;
  @Output() imported = new EventEmitter<void>();

  showImport = false;
  isDownloadingTemplate = false;
  isReadingExcel = false;
  isImporting = false;
  isDraggingExcel = false;
  selectedFile: File | null = null;
  previewSheetName = '';
  previewHeaders: string[] = [];
  previewRows: ExcelPreviewValue[][] = [];
  importError: { summary: string; details: string[] } | null = null;
  readonly templateHeaders = [...PDRH_TEMPLATE_HEADERS];

  private readonly maxExcelSize = 10 * 1024 * 1024;

  downloadTemplate(): void {
    const empresaId = this.normalizedEmpresaId;
    if (!empresaId || this.isDownloadingTemplate) return;

    this.isDownloadingTemplate = true;
    this.planoRecrutamentoService
      .downloadTemplateEmpresaSector(empresaId)
      .pipe(finalize(() => (this.isDownloadingTemplate = false)))
      .subscribe({
        next: (blob) => {
          const url = URL.createObjectURL(blob);
          const anchor = document.createElement('a');
          anchor.href = url;
          anchor.download = 'PDRH_PlanoRecrutamento.xlsx';
          document.body.appendChild(anchor);
          anchor.click();
          anchor.remove();
          setTimeout(() => URL.revokeObjectURL(url), 0);

          this.messageService.add({
            severity: 'success',
            summary: 'Template transferido',
            detail:
              'Preencha o ficheiro e volte para carregar o plano de necessidades.',
            life: 3500,
          });
        },
        error: () => {
          this.messageService.add({
            severity: 'error',
            summary: 'Erro',
            detail: 'Não foi possível baixar o template Excel.',
            life: 3500,
          });
        },
      });
  }

  openImport(): void {
    if (!this.normalizedEmpresaId) return;
    this.resetImport();
    this.showImport = true;
  }

  onExcelInputChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    input.value = '';
    if (file) void this.readExcel(file);
  }

  onExcelDragOver(event: DragEvent): void {
    event.preventDefault();
    this.isDraggingExcel = true;
  }

  onExcelDragLeave(event: DragEvent): void {
    event.preventDefault();
    this.isDraggingExcel = false;
  }

  onExcelDrop(event: DragEvent): void {
    event.preventDefault();
    this.isDraggingExcel = false;
    const file = event.dataTransfer?.files?.[0];
    if (file) void this.readExcel(file);
  }

  submitExcel(): void {
    const empresaId = this.normalizedEmpresaId;
    if (!empresaId || !this.selectedFile || !this.previewRows.length) return;

    this.isImporting = true;
    this.importError = null;
    this.planoRecrutamentoService
      .importarNecessidades(empresaId, this.selectedFile)
      .pipe(finalize(() => (this.isImporting = false)))
      .subscribe({
        next: () => {
          const importedRows = this.previewRows.length;
          this.showImport = false;
          this.clearImportState();
          this.imported.emit();
          this.messageService.add({
            severity: 'success',
            summary: 'Plano carregado',
            detail: `${importedRows} linha(s) enviada(s) com sucesso.`,
            life: 4000,
          });
        },
        error: (err: unknown) => {
          const errorResult = this.parseBackendError(err);
          this.importError = errorResult;
          const toastDetail = errorResult.details.length
            ? errorResult.details.join(' | ')
            : 'Não foi possível importar o plano. Verifique o template e tente novamente.';

          this.messageService.add({
            severity: 'error',
            summary: errorResult.summary || 'Erro na importação',
            detail: toastDetail,
            life: 6000,
          });
        },
      });
  }

  resetImport(): void {
    if (this.isImporting) return;
    this.clearImportState();
  }

  private clearImportState(): void {
    this.selectedFile = null;
    this.previewSheetName = '';
    this.previewHeaders = [];
    this.previewRows = [];
    this.isReadingExcel = false;
    this.isDraggingExcel = false;
    this.importError = null;
  }

  formatPreviewValue(value: ExcelPreviewValue): string {
    if (value instanceof Date) {
      return new Intl.DateTimeFormat('pt-AO').format(value);
    }
    if (value === null || value === undefined || value === '') return '—';
    return String(value);
  }

  formatFileSize(bytes: number): string {
    return bytes < 1024 * 1024
      ? `${Math.ceil(bytes / 1024)} KB`
      : `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }

  private get normalizedEmpresaId(): number | null {
    const empresaId = Number(this.empresaId);
    return Number.isInteger(empresaId) && empresaId > 0 ? empresaId : null;
  }

  private async readExcel(file: File): Promise<void> {
    if (!/\.(xlsx|xls)$/i.test(file.name)) {
      this.showExcelError(
        'Selecione um ficheiro Excel nos formatos .xlsx ou .xls.',
      );
      return;
    }

    if (file.size > this.maxExcelSize) {
      this.showExcelError('O ficheiro não pode ultrapassar 10 MB.');
      return;
    }

    this.isReadingExcel = true;
    this.selectedFile = null;
    this.previewHeaders = [];
    this.previewRows = [];
    this.importError = null;

    try {
      const XLSX = await import('xlsx');
      const workbook = XLSX.read(await file.arrayBuffer(), {
        type: 'array',
        cellDates: true,
      });
      const sheetName = workbook.SheetNames[0];
      const sheet = sheetName ? workbook.Sheets[sheetName] : null;

      if (!sheet) throw new Error('O ficheiro não contém folhas disponíveis.');

      const matrix = XLSX.utils.sheet_to_json(sheet, {
        header: 1,
        defval: '',
        blankrows: false,
        raw: true,
      }) as ExcelPreviewValue[][];
      const nonEmptyRows = matrix.filter((row) =>
        row.some((value) => value !== null && String(value).trim() !== ''),
      );

      if (nonEmptyRows.length < 2) {
        throw new Error(
          'O ficheiro não contém linhas de necessidades para importar.',
        );
      }

      const headerRow = nonEmptyRows[0];
      this.validateTemplateHeaders(headerRow);

      const unexpectedDataColumn = nonEmptyRows
        .slice(1)
        .some((row) =>
          row
            .slice(PDRH_TEMPLATE_HEADERS.length)
            .some((value) => this.hasValue(value)),
        );
      if (unexpectedDataColumn) {
        throw new Error(
          `Foram encontrados dados depois da coluna “${PDRH_TEMPLATE_HEADERS[PDRH_TEMPLATE_HEADERS.length - 1]}”. ` +
            'Utilize apenas as 11 colunas do novo template PDRH.',
        );
      }

      this.previewHeaders = [...PDRH_TEMPLATE_HEADERS];
      this.previewRows = nonEmptyRows
        .slice(1)
        .map((row) =>
          Array.from(
            { length: PDRH_TEMPLATE_HEADERS.length },
            (_, index) => row[index] ?? '',
          ),
        );
      this.previewSheetName = sheetName;
      this.selectedFile = file;
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : 'Não foi possível ler o ficheiro Excel.';
      this.showExcelError(message);
    } finally {
      this.isReadingExcel = false;
    }
  }

  private validateTemplateHeaders(headerRow: ExcelPreviewValue[]): void {
    const mismatchedHeaders = PDRH_TEMPLATE_HEADERS.flatMap(
      (expected, index) => {
        const received = String(headerRow[index] ?? '').trim();
        return this.normalizeHeader(received) === this.normalizeHeader(expected)
          ? []
          : [`coluna ${index + 1}: “${expected}”`];
      },
    );
    const hasUnexpectedHeaders = headerRow
      .slice(PDRH_TEMPLATE_HEADERS.length)
      .some((value) => this.hasValue(value));

    if (!mismatchedHeaders.length && !hasUnexpectedHeaders) return;

    const details = mismatchedHeaders.length
      ? `Cabeçalhos esperados: ${mismatchedHeaders.join('; ')}.`
      : 'O ficheiro possui colunas adicionais.';
    throw new Error(
      `O ficheiro não corresponde ao novo template PDRH. ${details} ` +
        'Baixe novamente o template e mantenha os nomes e a ordem das 11 colunas.',
    );
  }

  private normalizeHeader(value: string): string {
    return value
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[º°]/g, 'o')
      .replace(/[^a-zA-Z0-9]+/g, ' ')
      .trim()
      .toLowerCase();
  }

  private hasValue(value: ExcelPreviewValue): boolean {
    return value !== null && value !== undefined && String(value).trim() !== '';
  }

  private showExcelError(detail: string): void {
    this.selectedFile = null;
    this.previewHeaders = [];
    this.previewRows = [];
    this.messageService.add({
      severity: 'error',
      summary: 'Ficheiro inválido',
      detail,
      life: 4500,
    });
  }

  private parseBackendError(err: any): { summary: string; details: string[] } {
    let payload: any = null;

    if (err?.error) {
      payload = err.error;
    }

    if (!payload && err?.message && typeof err.message === 'string') {
      try {
        const parsed = JSON.parse(err.message);
        payload = parsed?.error || parsed;
      } catch {
        // Not JSON string
      }
    }

    if (!payload && typeof err === 'string') {
      try {
        const parsed = JSON.parse(err);
        payload = parsed?.error || parsed;
      } catch {
        // Not JSON string
      }
    }

    if (!payload && err && typeof err === 'object') {
      payload = err;
    }

    let summary = 'Erro na importação';
    const details: string[] = [];

    if (payload) {
      if (payload.retorno?.mensagem) {
        summary = payload.retorno.mensagem;
      } else if (payload.mensagem) {
        summary = payload.mensagem;
      } else if (
        payload.message &&
        typeof payload.message === 'string' &&
        !payload.message.startsWith('Http failure')
      ) {
        summary = payload.message;
      }

      if (Array.isArray(payload.data)) {
        payload.data.forEach((item: any) => {
          if (typeof item === 'string' && item.trim()) {
            details.push(item.trim());
          } else if (item && typeof item === 'object') {
            const itemMsg =
              item.mensagem || item.message || JSON.stringify(item);
            details.push(String(itemMsg).trim());
          }
        });
      } else if (typeof payload.data === 'string' && payload.data.trim()) {
        details.push(payload.data.trim());
      } else if (Array.isArray(payload.errors)) {
        payload.errors.forEach((item: any) => {
          if (typeof item === 'string' && item.trim()) {
            details.push(item.trim());
          } else if (item && typeof item === 'object') {
            const itemMsg =
              item.mensagem || item.message || JSON.stringify(item);
            details.push(String(itemMsg).trim());
          }
        });
      } else if (typeof payload.detail === 'string' && payload.detail.trim()) {
        details.push(payload.detail.trim());
      }
    }

    return { summary, details };
  }
}
